/**
 * Codes the paste produced that match no unit the user can see.
 *
 * These are shown rather than dropped: a typo'd code in a manifest is a unit
 * that would otherwise go silently missing from the assignment. Near-misses
 * offer a one-click correction; anything else can be edited in place.
 */

import { useState } from 'react';
import { Pencil } from 'lucide-react';
import type { UnmatchedCode } from './types';

interface UnmatchedListProps {
  items: UnmatchedCode[];
  onReplace: (oldCode: string, newCode: string) => void;
}

export function UnmatchedList({ items, onReplace }: UnmatchedListProps) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  if (!items.length) return null;

  const commit = (code: string) => {
    if (draft.trim()) onReplace(code, draft);
    setEditing(null);
  };

  return (
    <div className="flex flex-col gap-1.5 border-t border-border pt-3">
      <span className="text-[11px] font-medium uppercase tracking-wide text-yellow-400">
        Not matched · {items.length}
      </span>

      {items.map((item) => (
        <div
          key={item.code}
          className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-[7px] transition-colors hover:border-muted-foreground/50"
        >
          <span className="flex-none font-mono text-[13px] text-foreground">{item.code}</span>

          {editing === item.code ? (
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit(item.code);
                if (e.key === 'Escape') setEditing(null);
              }}
              onBlur={() => commit(item.code)}
              className="h-[26px] flex-1 rounded border border-[#8FB560] bg-background px-2 font-mono text-xs text-foreground outline-none"
            />
          ) : (
            <span className="min-w-0 flex-1 text-[11px] text-muted-foreground">
              {item.kind === 'ambiguous' ? 'Matches more than one unit. Pick one.' : 'No such unit in your fleet'}
            </span>
          )}

          {editing !== item.code &&
            (item.kind === 'ambiguous' ? item.candidates ?? [] : item.suggestion ? [item.suggestion] : []).map((candidate) => (
              <button
                key={candidate}
                type="button"
                onClick={() => onReplace(item.code, candidate)}
                className="h-[26px] flex-none whitespace-nowrap rounded-md bg-[#8FB560]/15 px-2.5 text-xs font-medium text-[#8FB560] transition-colors hover:bg-[#8FB560]/25"
              >
                Use {candidate}
              </button>
            ))}

          <button
            type="button"
            onClick={() => {
              setEditing(item.code);
              setDraft(item.code);
            }}
            title="Edit code"
            className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
          >
            <Pencil className="h-[13px] w-[13px]" />
          </button>
        </div>
      ))}
    </div>
  );
}
